import { createContext, FC, useReducer } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api/generated';
import * as models from '../models';
import { ILocationItem } from './custom/LocationTree';

interface IContext {
    state: ILocationContext;
    getLocations: (organizationId: number) => Promise<void>;
    getSelectedLocation: (locationId: number) => void;
    getDevices: (organizationId: number, locationId: number) => Promise<void>;
    setSelectedDeviceId: (deviceId: number | null) => void;
    deleteLocation: (locationId: number, organizationId: number) => Promise<void>;
}

interface ILocationContext {
    locations: ILocationItem[] | null;
    isLocationSelected: boolean;
    selectedLocation: ILocationItem | null;
    assignedDevices: models.IDevice[] | null;
    unassignedDevices: models.IDevice[] | null;
    selectedDeviceId: number | null;
}

const initialState: ILocationContext = {
    locations: null,
    isLocationSelected: false,
    selectedLocation: null,
    assignedDevices: null,
    unassignedDevices: null,
    selectedDeviceId: null
};

const GET_LOCATIONS = 'context/location/GET_LOCATIONS';
const GET_SELECTED_LOCATION = 'context/location/GET_SELECTED_LOCATION';
const GET_SELECTED_LOCATION_SUCCESS = 'context/location/GET_SELECTED_LOCATION_SUCCESS';
const GET_DEVICES = 'context/location/GET_DEVICES';
const SET_SELECTED_DEVICE_ID = 'context/location/SET_SELECTED_DEVICE_ID';
const DELETE_LOCATION = 'context/location/DELETE_LOCATION';

const actions = {
    setLocations: (locations: ILocationItem[]) => ({
        type: GET_LOCATIONS as typeof GET_LOCATIONS,
        payload: locations
    }),
    getSelectedLocation: () => ({
        type: GET_SELECTED_LOCATION as typeof GET_SELECTED_LOCATION
    }),
    getSelectedLocationSuccess: (location: ILocationItem | null) => ({
        type: GET_SELECTED_LOCATION_SUCCESS as typeof GET_SELECTED_LOCATION_SUCCESS,
        payload: location
    }),

    getDevices: (assigned: models.IDevice[], unassigned: models.IDevice[]) => ({
        type: GET_DEVICES as typeof GET_DEVICES,
        payload: { assigned, unassigned }
    }),
    setSelectedDeviceId: (deviceId: number | null) => ({
        type: SET_SELECTED_DEVICE_ID as typeof SET_SELECTED_DEVICE_ID,
        payload: deviceId
    }),
    deleteLocation: (locationId: number, organizationId: number) => ({
        type: DELETE_LOCATION as typeof DELETE_LOCATION,
        payload: { locationId, organizationId }
    })
};

type ActionTypes = ActionUnion<typeof actions>;

const reducer = (state: ILocationContext, action: ActionTypes): ILocationContext => {
    switch (action.type) {
        case GET_LOCATIONS:
            return {
                ...state,
                locations: action.payload
            };
        case GET_SELECTED_LOCATION:
            return {
                ...state,
                isLocationSelected: true,
                selectedLocation: null,
                assignedDevices: null,
                unassignedDevices: null
            };
        case GET_SELECTED_LOCATION_SUCCESS:
            return {
                ...state,
                selectedLocation: action.payload
            };
        case GET_DEVICES:
            return {
                ...state,
                assignedDevices: action.payload.assigned,
                unassignedDevices: action.payload.unassigned
            };
        case SET_SELECTED_DEVICE_ID:
            return {
                ...state,
                selectedDeviceId: action.payload
            };
        case DELETE_LOCATION:
            return {
                ...state,
                isLocationSelected: false,
                selectedLocation: null
            };
        default:
            return state;
    }
};

const LocationContext = createContext<IContext>({} as IContext);

const LocationContextProvider: FC = (props) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const getLocations = async (organizationId: number) => {
        try {
            const locations: ILocationItem[] = await api.administration.getLocations(null);
            dispatch({ type: GET_LOCATIONS, payload: locations });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };
    const getSelectedLocation = async (locationId: number) => {
        try {
            dispatch({ type: GET_SELECTED_LOCATION });
            const location = await api.administration.getLocation(locationId);
            dispatch({ type: GET_SELECTED_LOCATION_SUCCESS, payload: location });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };
    const getDevices = async (organizationId: number, locationId: number) => {
        try {
            const resp = await Promise.all([
                api.administration.getDevicesByLocationId({
                    locationId: locationId,
                    organizationInformationId: organizationId
                }),
                api.administration.getDevicesByLocationId({
                    locationId: null,
                    organizationInformationId: organizationId
                })
            ]);

            const assigned = resp[0];
            const unassigned = resp[1];

            dispatch({ type: GET_DEVICES, payload: { assigned, unassigned } });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };
    const setSelectedDeviceId = (deviceId: number | null) => {
        dispatch({
            type: SET_SELECTED_DEVICE_ID,
            payload: deviceId
        });
    };
    const deleteLocation = async (locationId: number, organizationId: number) => {
        try {
            await api.administration.deleteLocation(locationId);
            dispatch({
                type: DELETE_LOCATION,
                payload: {
                    organizationId,
                    locationId
                }
            });
            toast.success('Lokacija sėkmingai ištrinta');
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    return (
        <LocationContext.Provider value={{ state, getLocations, getSelectedLocation, getDevices, deleteLocation, setSelectedDeviceId }}>
            {props.children}
        </LocationContext.Provider>
    );
};

export { LocationContextProvider, LocationContext };
